import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Shuffle, PaintBucket, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { http, login, money, scrollToId } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RevealLines, FadeUp } from "@/components/Reveal";
import { BraceletPreview } from "@/components/BraceletPreview";
import { readCharm, clearCharm } from "@/hooks/useCharm";

const readPreset = () => {
  const saved = sessionStorage.getItem("lz_preset");
  try { return saved ? JSON.parse(saved) : null; } catch { return null; }
};

const Palette = ({ colors, brush, setBrush }) => (
  <div className="grid grid-cols-6 md:grid-cols-8 gap-2.5 md:gap-3" data-testid="designer-palette">
    {colors.map((c) => (
      <button key={c.hex} onClick={() => setBrush(c.hex)} title={c.name} aria-label={c.name}
        data-testid={`designer-color-${c.name.toLowerCase().replace(/\s/g, "-")}`}
        className={`aspect-square rounded-full transition-transform duration-300 hover:scale-110 ring-offset-2 ring-offset-[#170a24] ${brush === c.hex ? "ring-2 ring-amber-200 scale-110" : "ring-1 ring-white/20"}`}
        style={{ background: `radial-gradient(circle at 35% 30%, #ffffffb0, ${c.hex} 45%, ${c.hex})` }} />
    ))}
  </div>
);

const Tool = ({ icon: Icon, label, onClick, id }) => (
  <button onClick={onClick} data-testid={`designer-${id}-btn`}
    className="inline-flex items-center gap-2 rounded-full border border-purple-200/30 px-4 py-2 text-sm hover:border-amber-200 hover:text-amber-200 transition-colors">
    <Icon className="w-4 h-4" /> {label}
  </button>
);

export const Designer = () => {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [beads, setBeads] = useState([]);
  const [brush, setBrush] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [charm, setCharm] = useState(readCharm);
  const [charBg, setCharBg] = useState(() => sessionStorage.getItem("lz_char_bg"));
  const presetRef = useRef(null);
  if (presetRef.current === null) presetRef.current = readPreset() ?? false;

  useEffect(() => {
    http.get("/designs/palette").then((r) => {
      const d = r.data;
      if (!Array.isArray(d?.colors) || !d.colors.length) return;
      setCfg(d);
      setBrush(d.colors[1].hex);
      setBeads((prev) => (prev.length ? prev : Array.from({ length: d.bead_count }, (_, i) => d.colors[i % 2 ? 2 : 1].hex)));
    }).catch(() => setCfg(null));
  }, []);

  useEffect(() => {
    const onPreset = (e) => { if (Array.isArray(e.detail)) setBeads(e.detail); };
    window.addEventListener("lz:preset", onPreset);
    return () => window.removeEventListener("lz:preset", onPreset);
  }, []);

  useEffect(() => {
    const onCharm = (e) => setCharm(e.detail ?? null);
    const onBg = (e) => setCharBg(e.detail ?? null);
    window.addEventListener("lz:charm", onCharm);
    window.addEventListener("lz:charbg", onBg);
    return () => { window.removeEventListener("lz:charm", onCharm); window.removeEventListener("lz:charbg", onBg); };
  }, []);

  useEffect(() => {
    if (!cfg || !presetRef.current) return undefined;
    const b = presetRef.current;
    sessionStorage.removeItem("lz_preset");
    if (Array.isArray(b) && b.length === cfg.bead_count) setBeads(b);
    const t = setTimeout(() => scrollToId("design"), 300);
    return () => clearTimeout(t);
  }, [cfg]);

  if (!cfg) return null;
  const colorName = (hex) => cfg.colors.find((c) => c.hex === hex)?.name ?? "";
  const paint = (i) => setBeads((b) => b.map((x, j) => (j === i ? brush : x)));
  const fill = () => setBeads((b) => b.map(() => brush));
  const shuffle = () => setBeads((b) => b.map(() => cfg.colors[Math.floor(Math.random() * cfg.colors.length)].hex));
  const reset = () => { setBeads((b) => b.map((_, i) => cfg.colors[i % 2 ? 2 : 1].hex)); clearCharm(); setCharm(null); sessionStorage.removeItem("lz_char_bg"); setCharBg(null); };

  const order = async () => {
    if (!user) { login(); return; }
    setBusy(true);
    try {
      const { data } = await http.post("/designs/checkout", {
        beads, note, origin_url: window.location.origin, charm_title: charm?.title ?? "", charm_text: charm?.text ?? "",
      });
      if (data?.checkout_url) window.location.href = data.checkout_url;
      else throw new Error("no url");
    } catch {
      toast.error("Couldn't open checkout. Please try again.");
      setBusy(false);
    }
  };

  return (
    <section id="design" className="relative py-24 md:py-36 overflow-hidden" data-testid="designer-section">
      <img src="/img/flower-field.jpg" alt="" data-testid="designer-default-bg" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0e0616] via-[#0e0616]/35 to-[#0e0616]" />
      <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <div className="lg:col-span-6 lg:order-2">
          <motion.div initial={{ opacity: 0, scale: 0.92 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="relative overflow-hidden rounded-[28px] border border-purple-200/25 bg-[#0e0616]/55 backdrop-blur-xl p-4 md:p-8">
            {charBg && (
              <>
                <img src={charBg} alt="" data-testid="designer-character-bg"
                  className="absolute inset-0 w-full h-full object-cover object-[center_20%]" />
                <div className="absolute inset-0 bg-[#0e0616]/30" aria-hidden="true" />
              </>
            )}
            <div className="relative">
            <BraceletPreview beads={beads} onBead={paint} />
            <p className="text-center text-sm text-purple-50 mt-2 [text-shadow:0_1px_6px_rgba(0,0,0,0.9)]">Tap any bead to paint it with <span className="text-amber-200">{colorName(brush)}</span></p>
            </div>
          </motion.div>
        </div>
        <div className="lg:col-span-6 lg:order-1">
          <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-purple-100 mb-6">Design your own</p>
          <h2 className="font-display text-5xl md:text-7xl leading-[0.92] tracking-tight">
            <RevealLines inView lines={[<>Choose every</>, <em className="text-amber-200">bead yourself.</em>]} />
          </h2>
          <FadeUp className="mt-6 max-w-md text-purple-50/90 leading-relaxed">
            Pick your colors and Liza will string your bracelet by hand, exactly as you designed it.
          </FadeUp>
          <div className="mt-10 rounded-[22px] border border-purple-200/25 bg-[#170a24]/80 backdrop-blur-xl p-6 md:p-8 space-y-6">
            <Palette colors={cfg.colors} brush={brush} setBrush={setBrush} />
            <div className="flex flex-wrap gap-2">
              <Tool icon={PaintBucket} label="Fill all" onClick={fill} id="fill" />
              <Tool icon={Shuffle} label="Surprise me" onClick={shuffle} id="shuffle" />
              <Tool icon={RotateCcw} label="Reset" onClick={reset} id="reset" />
            </div>
            {charm?.text && (
              <div className="rounded-xl border border-amber-200/30 bg-[#0e0616]/60 px-4 py-3" data-testid="designer-charm">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-200">{charm.title} · written on your package</p>
                <p className="mt-1 text-sm text-purple-50/90" data-testid="designer-charm-text">{charm.text}</p>
              </div>
            )}
            <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} rows={2} data-lenis-prevent
              data-testid="designer-note-input" placeholder="A note for Liza (wrist size, a wish…)"
              className="w-full rounded-xl bg-[#0e0616]/60 border border-purple-200/20 px-4 py-3 text-sm outline-none focus:border-purple-200 resize-none placeholder:text-purple-200/70" />
            <div className="flex items-center justify-between gap-4">
              <span className="font-mono text-2xl text-amber-200" data-testid="designer-price">{money(cfg.price)}</span>
              <button onClick={order} disabled={busy} data-testid="designer-order-btn"
                className="rounded-full bg-purple-200 text-[#0e0616] px-7 py-3.5 font-medium hover:bg-amber-200 transition-colors disabled:opacity-60">
                {busy ? "Opening checkout…" : user ? "Order my design" : "Sign in to order"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
